import * as React from "react";
import {
  ConfirmationDialog,
  ConfirmationOptions,
} from "./../../components/controls/ConfirmationDialog/ConfirmationDialog";

const ConfirmationContext = React.createContext<
  (options: ConfirmationOptions) => Promise<void>
>(Promise.reject);

export const useConfirmation = () => React.useContext(ConfirmationContext);

export const ConfirmationProvider: React.FC = ({ children }) => {
  const [
    confirmationState,
    setConfirmationState,
  ] = React.useState<ConfirmationOptions | null>(null);
  const [open, setOpen] = React.useState(false);

  const awaitingPromiseRef = React.useRef<{
    resolve: () => void;
    reject: () => void;
  }>();

  const openConfirmation = (options: ConfirmationOptions) => {
    setConfirmationState(options);
    setOpen(true);
    return new Promise<void>((resolve, reject) => {
      awaitingPromiseRef.current = { resolve, reject };
    });
  };

  const handleCancel = () => {
    if (confirmationState?.catchOnCancel && awaitingPromiseRef.current) {
      awaitingPromiseRef.current.reject();
    }
    setOpen(false);
  };

  const handleSubmit = () => {
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve();
    }
    setOpen(false);
  };

  const handleExited = () => {
    setConfirmationState(null);
  };

  return (
    <>
      <ConfirmationContext.Provider
        value={openConfirmation}
        children={children}
      />

      <ConfirmationDialog
        open={open}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onExited={handleExited}
        {...confirmationState}
      />
    </>
  );
};
